import { ColumnDef, Getter, Row } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { ButtonData } from '../../../../components/buttonGroup/ButtonGroup';
import { SimpleButton } from '../../../../components/buttons/SimpleButton';
import { BadgeCell } from '../../../../components/tables/cellComponents/BadgeCell';
import { ExpanderCell } from '../../../../components/tables/cellComponents/ExpanderCell';
import { LinkCell } from '../../../../components/tables/cellComponents/LinkCell';
import { FilterField, FilterForm } from '../../../../components/tables/filterForms/FilterForm';
import { FormSelect } from '../../../../components/tables/filterForms/FormSelect';
import SFCTable from '../../../../components/tables/SFCTable';
import { useDataContext } from '../../../../context';
import { useToast } from '../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../hooks';
import {
  Scopes,
  SFCLocation,
  useGetAdminsQuery,
  useMimicMutation,
  User
} from '../../../../services/endpoints/people/user';
import { getCurrentUser } from '../../../../services/helper';
import { userTypeNames, userTypes, usTimezones } from '../../../../utils/consts';
import { getFilterValue } from '../../../../utils/listFilterSearchParams';
import { getErrorMessage, getKeyByValue } from '../../../../utils/utils';

interface DataRow extends User {
  log_in_as: (id: number) => void;
}

const defaultScope = 'admins';

export const userCanEditCreateDelete_AdminUser = (user: User | null) => {
  if (
    user?.isSuper ||
    user?.isSupremeAdmin ||
    user?.isAdmin ||
    user?.isGlobal ||
    user?.isLocationAdmin
  ) {
    return true;
  }

  return false;
};

export const userCanView_AdminUser = (user: User | null) => {
  if (
    user?.isSuper ||
    user?.isSupremeAdmin ||
    user?.isAdmin ||
    user?.isGlobal ||
    user?.isLocationAdmin ||
    user?.isInstructor
  ) {
    return true;
  }

  return false;
};

export const userCanLoginAs = (user: User | null) => {
  if (user?.isSuper || user?.isSupremeAdmin) {
    return true;
  }

  return false;
};

export const AdminsView = () => {
  const { addToast } = useToast();
  const { locationOptions } = useDataContext();
  const [filters, setFilters] = useListFiltersInUrl({ scope: defaultScope });
  const { data, error, isLoading, isFetching } = useGetAdminsQuery(filters);
  const [scopes, setScopes] = useState<Scopes>({});
  const [users, setUsers] = useState<User[]>([]);
  const [buttons, setButtons] = useState<ButtonData[][]>();
  const navigate: NavigateFunction = useNavigate();
  const [mimic] = useMimicMutation();
  const currentUser = getCurrentUser();
  const canMimic = userCanLoginAs(currentUser);
  const [showUserType, setShowUserType] = useState(false);
  let filterFormFields: FilterField[] = [
    {
      name: 'id',
      label: 'ID',
      type: 'number',
      value: getFilterValue(filters.data?.filterBy, 'id')
    },
    {
      name: 'email',
      label: 'Email',
      value: getFilterValue(filters.data?.filterBy, 'email')
    },
    {
      name: 'first_name',
      label: 'First Name',
      value: getFilterValue(filters.data?.filterBy, 'first_name')
    },
    {
      name: 'last_name',
      label: 'Last Name',
      value: getFilterValue(filters.data?.filterBy, 'last_name')
    },
    {
      name: 'city',
      label: 'City',
      value: getFilterValue(filters.data?.filterBy, 'city')
    },
    {
      name: 'state',
      label: 'State',
      value: getFilterValue(filters.data?.filterBy, 'state')
    },
    {
      name: 'zip',
      label: 'Zip',
      value: getFilterValue(filters.data?.filterBy, 'zip')
    },
    {
      name: 'phone',
      label: 'Phone',
      value: getFilterValue(filters.data?.filterBy, 'phone')
    },
    {
      name: 'timezone',
      label: 'Timezone',
      element: (
        <FormSelect
          name="timezone"
          label="Timezone"
          options={usTimezones.map((tz) => ({ value: tz, label: tz }))}
          selected_Value={usTimezones
            .map((tz) => ({ value: tz, label: tz }))
            .find((option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'timezone'))}
        />
      )
    }
  ];

  if (currentUser?.isGlobal) {
    filterFormFields.push({
      name: 'location_id',
      type: 'number',
      label: 'Location',
      element: (
        <FormSelect
          name="location_id"
          label="Location"
          options={locationOptions}
          selected_Value={locationOptions.find(
            (option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'location_id')
          )}
        />
      )
    });
  }
  const columns: ColumnDef<DataRow>[] = [
    {
      id: 'expander',
      enableSorting: false,
      cell: ({ row }) =>
        row.original?.children && row.original?.children?.length > 0 ? (
          <ExpanderCell row={row} />
        ) : null
    },
    {
      header: 'ID',
      accessorKey: 'id',
      cell: ({ getValue, row }) => (
        <LinkCell
          content={getValue<string>()}
          url={`/admin/dash-users/admins/${row.original.id}`}
        />
      )
    },
    {
      header: 'First Name',
      accessorKey: 'first_name'
      // cell: ({ getValue, row }) => (
      //   <LinkCell content={getValue<string>()} url={`/admin/dash-users/admins/${row.original.id}`} />
      // ),
    },
    {
      header: 'Last Name',
      accessorKey: 'last_name'
    },
    {
      header: 'Phone',
      accessorKey: 'phone'
    },
    {
      header: 'Email',
      accessorKey: 'email'
    },
    ...(!currentUser?.isInstructor
      ? ([
          {
            header: 'Location',
            accessorKey: 'location',
            enableSorting: false,
            cell: ({ getValue }) => <span className="">{getValue<SFCLocation>()?.title}</span>
          }
        ] as ColumnDef<DataRow>[])
      : []),
    {
      header: 'User Type',
      accessorKey: 'user_type_id',
      cell: ({ getValue }) => (
        <span className="">{userTypeNames[getKeyByValue(userTypes, getValue<number>())!]}</span>
      )
    },
    {
      header: 'Active',
      accessorKey: 'active',
      cell: ({ getValue }) => <BadgeCell active={getValue<boolean>()} />
    },
    ...(canMimic
      ? [
          {
            header: 'Impersonate',
            accessorKey: 'log_in_as',
            enableSorting: false,
            cell: ({ getValue, row }: { getValue: Getter<unknown>; row: Row<DataRow> }) => (
              <Button
                size="sm"
                variant="sfc-orange"
                className="text-truncate"
                onClick={() => getValue<(id: number) => void>()(row.original.id)}
              >
                Log in as...
              </Button>
            )
          }
        ]
      : [])
  ];

  const loginAs = (userId: number) => {
    mimic(userId)
      .unwrap()
      .then(
        (r) => {
          addToast('Logged in successfully', 'success');
          navigate('/');
        },
        (error: any) => {
          addToast(getErrorMessage(error), 'error');
        }
      );
  };

  useEffect(() => {
    if (!error && !isLoading && data && data.results) {
      if (data.scopes) {
        setScopes(
          data.scopes.reduce((acc: Scopes, curr: Scopes) => ({ ...acc, ...curr }), {} as Scopes)
        );
        setButtons(
          data.scopes.map((sMap) =>
            Object.entries(sMap).map(([scope, title]) => {
              let scope_count = 0;
              if (data.scope_counts != null) {
                scope_count = data.scope_counts[scope] ?? 0;
              }
              return {
                title: `${title} (${scope_count})`,
                key: scope
              };
            })
          )
        );
      }
    } else if (error) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      const results = data.results.map((user) => ({
        ...user,
        log_in_as: loginAs
      }));
      setUsers(results);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  return (
    <div className="py-2">
      <SFCTable
        name={scopes[filters?.scope!]}
        columns={showUserType ? columns : columns.filter((c) => c.header !== 'User Type')}
        data={users}
        count={data?.count}
        defaultScope={filters.scope ?? defaultScope}
        scopes={buttons}
        isLoading={isLoading}
        isFetching={isFetching}
        indexDownloadPath="users/admins"
        filters={filters?.data?.filterBy}
        filterForm={
          <FilterForm
            fields={filterFormFields}
            onApplyFilters={setFilterBy}
          />
        }
        onFiltersChanged={setFilters}
        onScopeChange={(scope) => {
          // console.log(scope);
          if (scope === 'all_active' || scope === 'inactive' || scope === 'unmapped') {
            setShowUserType(true);
          } else {
            setShowUserType(false);
          }
        }}
        newItemEl={
          userCanEditCreateDelete_AdminUser(currentUser) && (
            <SimpleButton
              variant="sfc-blueberry"
              height={40}
              truncate={false}
              onClick={() => navigate('/admin/dash-users/admins/new')}
            >
              New admin
            </SimpleButton>
          )
        }
      />
    </div>
  );
};
